import { Paper, Stack } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import EditableBox from '@/features/site/components/EditableBox'
import EditIconButton from '@/features/site/components/EditIconButton'
import SiteNotificationPreferencesSettingsDisplay from '@/features/site/components/settings/notification_preferences/SiteNotificationPreferencesSettingsDisplay'
import type { Site } from '@/features/site/types/site'

import { SiteNotificationPreferenceSettingsForm } from './form/SiteNotificationPreferenceSettingsForm'

type SiteSettingsProps = {
  site: Site
}

export const SiteNotificationPreferencesSettings = ({ site }: SiteSettingsProps) => {
  const { t } = useTranslation()
  const [isEditable, setIsEditable] = useState(false)
  const [isEmailEnabled, setIsEmailEnabled] = useState(site.notificationPreferences?.isEmailEnabled ?? null)
  const [isSmsEnabled, setIsSmsEnabled] = useState(site.notificationPreferences?.isSmsEnabled ?? null)

  function handleEmailEnabledChange(isEmailEnabled: boolean) {
    switchToEditMode()
    setIsEmailEnabled(isEmailEnabled)
  }

  function handleSmsEnabledChange(isSmsEnabled: boolean) {
    switchToEditMode()
    setIsSmsEnabled(isSmsEnabled)
  }

  function switchToEditMode() {
    setIsEditable(true)
  }

  function switchToReadMode() {
    setIsEditable(false)
    setIsSmsEnabled(null)
    setIsEmailEnabled(null)
  }

  return (
    <Paper sx={{ p: 3, height: 'fit-content', width: '100%' }}>
      <Stack gap={2}>
        <Stack alignItems="center" flexDirection="row" justifyContent="space-between">
          <CustomTypography variant="h5">{t('sites.add_new.steps.notifications')}</CustomTypography>

          {!isEditable && (
            <EditIconButton tooltipText={t('customer_details.tabs.settings.edit_label')} onClick={switchToEditMode} />
          )}
        </Stack>

        <EditableBox isEditable={isEditable}>
          {isEditable ? (
            <SiteNotificationPreferenceSettingsForm
              isEmailEnabled={isEmailEnabled}
              isSmsEnabled={isSmsEnabled}
              site={site}
              onReset={switchToReadMode}
              onSubmit={switchToReadMode}
            />
          ) : (
            <SiteNotificationPreferencesSettingsDisplay
              notificationPreferences={site.notificationPreferences}
              onEmailEnabledChange={handleEmailEnabledChange}
              onSmsEnabledChange={handleSmsEnabledChange}
            />
          )}
        </EditableBox>
      </Stack>
    </Paper>
  )
}
